import { SlotRequestAmountAndActions } from "@/components/common/section-components/requests/slot-request/SlotRequestAmountAndActions";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  SlotMainData,
  SlotRequestDetail,
  SlotSampleData,
} from "../schema/slot-requests.schema";
import { RequestTypes, MotiveTypes } from "@/constants/enums";
import { SlotRequestDetailMainDataCard } from "@/components/common/section-components/requests/slot-request/SlotRequestMainDataCard";
import { NegotiationLastMessage } from "@/components/common/section-components/chat/NegotiationLastMessage";
import { StandardTable } from "@/components/table/StandardTable";
import {
  fetchAssignedSlotsHandler,
  fetchSlotRequestHandler,
  handleAssignSlotsHandler,
  handleRejectSlotRequestHandler,
  handleSendSlotRequestInformationHandler,
} from "../schema/handler-functions";
import { FieldValues } from "react-hook-form";
import { useRoles } from "@/hooks/useRoles";
import { SlotRequestStatus, SlotStatus } from "@/constants/entity-status";

import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { assignedSlotsHeaders } from "../schema/header-elements";
export const SlotRequestDetailPage = () => {
  const { id } = useParams();

  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [loadingSlotRequest, setLoadingSlotRequest] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(AppRoutes.SLOT_REQUESTS);
  };

  const [loadingSendInfo, setLoadingSendInfo] = useState<boolean>(false);
  const [loadingAssignSlots, setLoadingAssignSlots] = useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [openAssignSlots, setOpenAssignSlots] = useState<boolean>(false);
  const [loadingRejectSlotRequest, setLoadingRejectSlotRequest] =
    useState<boolean>(false);

  const [slotRequest, setSlotRequest] = useState<SlotRequestDetail>();

  const [assignedSlots, setAssignedSlots] = useState<
    SlotMainData[] | undefined
  >();

  const { setOpen: setOpenInfoModal, open: openInfoModal } = useDisclosure();

  const { open: openChatViewModal, setOpen: setOpenChatViewModal } =
    useDisclosure();

  const fetchSlotRequest = async (showLoad = true) => {
    await fetchSlotRequestHandler({
      fetchPermissions,
      id: Number(id),
      navigate,
      setLoadingSlotRequest,
      setSlotRequest,
      showLoad,
    });
  };

  const fetchAssignedSlots = async () => {
    fetchAssignedSlotsHandler({
      slotRequest,
      setAssignedSlots,
    });
  };

  useEffect(() => {
    if (isActive && id) {
      fetchSlotRequest();
      fetchAssignedSlots();
    }
  }, [isActive]);

  useEffect(() => {
    if (slotRequest?.id) fetchAssignedSlots();
  }, [slotRequest]);

  const { fetchPermissions } = useRoles();

  const handleSendInfo = async (event?: FieldValues) => {
    // No se pasa setOpenInformationModal: el chat es multi-turno y debe
    // permanecer abierto después de enviar.
    return await handleSendSlotRequestInformationHandler({
      fetchPermissions,
      fetchSlotRequest,
      navigate,
      setLoadingRequestInformation: setLoadingSendInfo,
      event,
      slotRequest,
    });
  };

  const handleRejectSlotRequest = async (event?: FieldValues) => {
    handleRejectSlotRequestHandler({
      navigate,
      setLoadingRejectSlotRequest,
      setOpenRejectModal,
      event,
      slotRequest,
      fetchPermissions,
    });
  };

  const showSlots =
    assignedSlots?.length &&
    (slotRequest?.estado == SlotRequestStatus.SLOTS_ASSIGNED ||
      slotRequest?.estado == SlotRequestStatus.SLOTS_PARTIALLY_ASSIGNED ||
      slotRequest?.estado == SlotRequestStatus.NEEDS_INFO);

  const assignedSlotsItems =
    assignedSlots?.map((cupo) => ({
      ...cupo,
      fechaDescarga: cupo?.fechaDescarga ? cupo.fechaDescarga : "-",
      estado: <RequestStatusBadge status={cupo?.estado as SlotStatus} />,
    })) ?? [];

  const handleAssignSlots = async (selection: SlotSampleData[]) => {
    handleAssignSlotsHandler({
      fetchPermissions,
      fetchSlotRequest,
      navigate,
      setLoadingAssignSlotRequest: setLoadingAssignSlots,
      setOpenAssignSlotsModal: setOpenAssignSlots,
      slotRequest,
      selection,
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title={"Solicitud de Cupos"}
        onBack={handleBack}
        backButton
        loading={loadingSlotRequest}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="12px">
        {!loadingSlotRequest && (
          <>
            <SlotRequestAmountAndActions
              loadingSendInfo={loadingSendInfo}
              loadingAssignSlots={loadingAssignSlots}
              setOpenInfoModal={setOpenInfoModal}
              setOpenRejectModal={setOpenRejectModal}
              setOpenAssignSlots={setOpenAssignSlots}
              loadingRejectSlotRequest={loadingRejectSlotRequest}
              loadingSlotRequest={loadingSlotRequest}
              slotRequest={slotRequest}
            />

            <SlotRequestDetailMainDataCard slotRequest={slotRequest} />

            <NegotiationLastMessage
              title="Último mensaje - Requiere información"
              messages={slotRequest?.mensajes || []}
              onOpenChat={() => setOpenChatViewModal(true)}
              testId="slot-request-open-chat"
            />
            {Boolean(showSlots) && (
              <Flex flexDir="column" gap="8px" paddingBottom="12px">
                <Flex alignItems="center" justifyContent="space-between">
                  <Text
                    fontWeight={700}
                    css={{
                      "@media (max-width: 1350px) and (min-width: 1272px)": {
                        fontSize: "12px",
                      },
                      "@media (max-width: 1414px) and (min-width: 1351px)": {
                        fontSize: "14px",
                      },
                      "@media (max-width: 1590px) and (min-width: 1415px)": {
                        fontSize: "14px",
                      },
                      "@media (max-width: 1696px) and (min-width: 1591px)": {
                        fontSize: "16px",
                      },
                      "@media (max-width: 1920px)": {
                        fontSize: "16px",
                      },
                    }}
                  >
                    Cupos asignados
                  </Text>
                </Flex>
                <StandardTable
                  headers={assignedSlotsHeaders}
                  items={assignedSlotsItems}
                  noSelection
                  noActions
                  rowId="nroCupo"
                  fullWidth
                  maxHeight="380px"
                />
              </Flex>
            )}
          </>
        )}
      </Flex>
      {slotRequest?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectSlotRequest}
            loadingAccept={loadingRejectSlotRequest}
            idTipoSolicitud={RequestTypes.Cupos}
            entityName="solicitud de cupos"
          />
          <NegotiationChatModalDynamic
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            title="Chat - Requiere información"
            messages={slotRequest?.mensajes || []}
            handleSend={handleSendInfo}
            loadingSend={loadingSendInfo}
            idTipoSolicitud={RequestTypes.Cupos}
            tipoMotivo={MotiveTypes.RequiereInformacion}
            mensajesDisponibles={slotRequest?.mensajesDisponibles}
          />

          <NegotiationChatModalDynamic
            open={openChatViewModal}
            setOpen={setOpenChatViewModal}
            title="Chat - Requiere información"
            messages={slotRequest?.mensajes || []}
            readOnly
          />
          <AssignSlotsModalDynamic
            accountNumber={slotRequest?.cuentaOrigen}
            requestId={slotRequest?.id}
            loadingAccept={loadingAssignSlots}
            open={openAssignSlots}
            handleAssign={handleAssignSlots}
            setOpen={setOpenAssignSlots}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const NegotiationChatModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationChatModal").then((el) => ({
    default: el["NegotiationChatModal"],
  })),
);

const AssignSlotsModalDynamic = lazy(() =>
  import("@/components/modal/AssignSlotsModal").then((el) => ({
    default: el["AssignSlotsModal"],
  })),
);
